import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../hooks/useStore";
import { useAuth } from "../../hooks/useAuth";
import { connectSocket, disconnectSocket } from "../../services/socket";
import { useSocketEvent, useSocketResync } from "../../hooks/useSocketEvent";
import {
  notificationReceived,
  resetUnreadCountCache,
  fetchUnreadCount,
} from "../../store/slices/notificationSlice";
import { shipmentStatusPatched } from "../../store/slices/shipmentSlice";
import { modulesChanged } from "../../store/slices/modulesSlice";
import { containerLineMessagingPatched } from "../../store/slices/containerOrdersSlice";
import type { ContainerMessageThreadEvent } from "../../types/containerMessages";
import type { ModuleKey } from "../../constants/modules";
import type { Notification } from "../../types/notifications";

interface ModulesChanged {
  companyId: string;
  enabledModules: ModuleKey[];
}

interface ShipmentStatusChanged {
  shipmentId: string;
  status: string;
  shipmentNumber?: string;
  updatedAt?: string;
}

/**
 * Owns the app-wide socket lifetime and the listeners that aren't tied to any
 * one screen.
 *
 * Renders nothing, and holds no context: screens reach the socket through
 * `useSocketEvent`, which reads the same module singleton. Mounted inside
 * AppLayout, which is the shell for every authenticated route including the
 * driver branch.
 */
const SocketBridge: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAuth();

  // Drivers authenticate and connect (they need work:changed), but they have no
  // notification UI at all — /api/notifications is gated by
  // authorize(["admin","user"]) — so nothing here should toast at them.
  const wantsNotifications = Boolean(user) && user?.role !== "driver";

  useEffect(() => {
    if (!isAuthenticated) {
      disconnectSocket();
      return;
    }

    connectSocket();
    return () => disconnectSocket();
  }, [isAuthenticated]);

  /**
   * A superadmin changed this tenant's modules.
   *
   * Everyone gets this, drivers included — it arrives on the tenant room rather
   * than the dispatcher-only company room. Applying it re-renders the nav and
   * the route guards from the new set, so anyone standing on a screen that was
   * just switched off is moved to their home screen instead of being left
   * clicking into 404s until they next sign in.
   *
   * Enabled for every authenticated session, not just those wanting
   * notifications: a driver has no notification UI at all but is exactly who
   * this matters most for.
   */
  useSocketEvent<ModulesChanged>(
    "company:modules-changed",
    (payload) => {
      if (Array.isArray(payload?.enabledModules)) {
        dispatch(modulesChanged(payload.enabledModules));
      }
    },
    isAuthenticated,
  );

  useSocketEvent<Notification>(
    "notification:new",
    (notification) => {
      dispatch(notificationReceived(notification));
      toast.info(notification.message, { toastId: notification._id });
    },
    wantsNotifications,
  );

  // Handled here rather than per page: the reducer patches both the list row
  // and `currentShipment`, so the shipments list and the open detail view both
  // stay current without either component knowing about sockets.
  useSocketEvent<ShipmentStatusChanged>(
    "shipment:statusChanged",
    (payload) => {
      if (!payload?.shipmentId) return;
      dispatch(shipmentStatusPatched(payload));
    },
    wantsNotifications,
  );

  /**
   * A container's conversation moved — the counter, never the message.
   *
   * Handled app-wide rather than per screen because it feeds every list that
   * draws an unread badge, and several of them can be on screen at once (an
   * orders list with three batches expanded). The reducer finds the one
   * container it names and patches it; a dispatcher with nothing open pays
   * nothing.
   *
   * This is the compact half of the messaging channel. The message *body* is
   * emitted only into the room of the container it is about, which is joined by
   * the container page for as long as it is open — so a tenant running a
   * thousand containers puts one small counter on a dispatcher's socket per
   * message, and the words only where somebody is reading them.
   *
   * The bell is what covers a message nobody has open: a driver's message
   * raises a company-wide notification through the ordinary path above.
   */
  useSocketEvent<ContainerMessageThreadEvent>(
    "containerMessage:thread",
    (payload) => {
      if (!payload?.lineId) return;
      dispatch(
        containerLineMessagingPatched({
          lineId: payload.lineId,
          messaging: payload.messaging ?? {},
        }),
      );
    },
    wantsNotifications,
  );

  // Nothing is replayed after a dropped connection, so the count is refetched
  // rather than assumed. `resetUnreadCountCache` first, because fetchUnreadCount
  // short-circuits on the cached value otherwise.
  useSocketResync(() => {
    dispatch(resetUnreadCountCache());
    dispatch(fetchUnreadCount());
  }, wantsNotifications);

  return null;
};

export default SocketBridge;
